import { ref } from 'vue';
import { isDevMode } from '@/utils/devMode';

const AUTH_BASE_URL = 'https://auth.pappers.fr';
const CLIENT_ID = 'https://addon-word.pappers.fr/oauth/metadata.json';
const SCOPES = 'read:api_key read:user';
const ME_URL = 'https://auth.pappers.fr/me';

const STORAGE_KEYS = {
	accessToken: 'pappers-oauth-access-token',
	refreshToken: 'pappers-oauth-refresh-token',
	expiresAt: 'pappers-oauth-expires-at',
	organizationId: 'pappers-oauth-organization-id'
};

const DEV_STORAGE_KEYS = {
	apiKey: 'pappers-addon-api-key',
	organizationId: 'pappers-addon-org-id'
};

const devToken = ref<string | null>(null);
const isAuthenticated = ref(false);
const organizationId = ref<string | null>(null);

// --- PKCE helpers (Web Crypto API) ---

const base64UrlEncode = (buffer: Uint8Array): string => {
	let binary = '';
	for (const byte of buffer) {
		binary += String.fromCharCode(byte);
	}
	return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const generateRandomString = (length: number): string => {
	const array = new Uint8Array(length);
	crypto.getRandomValues(array);
	return base64UrlEncode(array);
};

const generateCodeChallenge = async (verifier: string): Promise<string> => {
	const data = new TextEncoder().encode(verifier);
	const digest = await crypto.subtle.digest('SHA-256', data);
	return base64UrlEncode(new Uint8Array(digest));
};

// --- Token management ---

const getStoredAccessToken = (): string | null => localStorage.getItem(STORAGE_KEYS.accessToken);
const getStoredRefreshToken = (): string | null => localStorage.getItem(STORAGE_KEYS.refreshToken);
const getStoredExpiresAt = (): number => Number(localStorage.getItem(STORAGE_KEYS.expiresAt) || '0');
const isTokenExpired = (): boolean => Date.now() >= getStoredExpiresAt();

const storeTokens = (accessToken: string, refreshToken: string, expiresIn: number) => {
	localStorage.setItem(STORAGE_KEYS.accessToken, accessToken);
	localStorage.setItem(STORAGE_KEYS.refreshToken, refreshToken);
	localStorage.setItem(STORAGE_KEYS.expiresAt, String(Date.now() + expiresIn * 1000));
};

const clearStorage = () => {
	for (const key of Object.values(STORAGE_KEYS)) {
		localStorage.removeItem(key);
	}
};

const clearDevStorage = () => {
	for (const key of Object.values(DEV_STORAGE_KEYS)) {
		localStorage.removeItem(key);
	}
};

// --- API calls ---

const fetchUserInfo = async (accessToken: string): Promise<string> => {
	const response = await fetch(`${AUTH_BASE_URL}/oauth/userinfo`, {
		headers: { Authorization: `Bearer ${accessToken}` }
	});
	if (!response.ok) {
		throw new Error(`Session expirée ou invalide (récupération des informations utilisateur)`);
	}
	const data = await response.json();
	return data.organization_uuid;
};

const fetchDevUserInfo = async (accessToken: string): Promise<string> => {
	const response = await fetch(ME_URL, {
		headers: { Authorization: `Bearer ${accessToken}` }
	});
	if (!response.ok) {
		throw new Error('Session expirée');
	}
	const data = await response.json();
	const orgId = data.organizationId || data.organization_id;
	if (!orgId) {
		throw new Error('Organization ID introuvable');
	}
	return orgId;
};

const exchangeCode = async (code: string): Promise<void> => {
	const codeVerifier = sessionStorage.getItem('pappers-oauth-code-verifier');
	if (!codeVerifier) {
		throw new Error('Code verifier manquant');
	}
	sessionStorage.removeItem('pappers-oauth-code-verifier');

	const redirectUri = `${window.location.origin}/callback.html`;
	const response = await fetch(`${AUTH_BASE_URL}/oauth/token`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({
			grant_type: 'authorization_code',
			code,
			redirect_uri: redirectUri,
			client_id: CLIENT_ID,
			code_verifier: codeVerifier
		})
	});

	if (!response.ok) {
		const text = await response.text().catch(() => '');
		throw new Error(`Erreur d'authentification: ${response.status}${text ? ` - ${text}` : ''}`);
	}

	const data = await response.json();
	storeTokens(data.access_token, data.refresh_token, data.expires_in);

	const orgId = await fetchUserInfo(data.access_token);
	organizationId.value = orgId;
	localStorage.setItem(STORAGE_KEYS.organizationId, orgId);
	isAuthenticated.value = true;
};

const refreshAccessToken = async (): Promise<void> => {
	const refreshToken = getStoredRefreshToken();
	if (!refreshToken) {
		throw new Error('Pas de refresh token');
	}

	const response = await fetch(`${AUTH_BASE_URL}/oauth/token`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({
			grant_type: 'refresh_token',
			refresh_token: refreshToken,
			client_id: CLIENT_ID
		})
	});

	if (!response.ok) {
		clearStorage();
		throw new Error(`Session expirée (refresh token invalide)`);
	}

	const data = await response.json();
	storeTokens(data.access_token, data.refresh_token, data.expires_in);
};

// --- Public API ---

export const useAuth = () => {
	const login = (): Promise<void> => {
		return new Promise((resolve, reject) => {
			const start = async () => {
				const codeVerifier = generateRandomString(32);
				const codeChallenge = await generateCodeChallenge(codeVerifier);
				const state = generateRandomString(16);

				sessionStorage.setItem('pappers-oauth-code-verifier', codeVerifier);
				sessionStorage.setItem('pappers-oauth-state', state);

				const redirectUri = `${window.location.origin}/callback.html`;
				const params = new URLSearchParams({
					response_type: 'code',
					client_id: CLIENT_ID,
					redirect_uri: redirectUri,
					code_challenge: codeChallenge,
					code_challenge_method: 'S256',
					scope: SCOPES,
					state
				});

				const authorizeUrl = `${AUTH_BASE_URL}/oauth/authorize?${params.toString()}`;

				Office.context.ui.displayDialogAsync(authorizeUrl, { height: 60, width: 30 }, (result) => {
					if (result.status === Office.AsyncResultStatus.Failed) {
						reject(new Error(result.error.message));
						return;
					}

					const dialog = result.value;
					dialog.addEventHandler(Office.EventType.DialogMessageReceived, async (arg: any) => {
						dialog.close();
						try {
							const message = JSON.parse(arg.message);
							const savedState = sessionStorage.getItem('pappers-oauth-state');
							sessionStorage.removeItem('pappers-oauth-state');

							if (message.state !== savedState) {
								reject(new Error('État OAuth invalide (CSRF)'));
								return;
							}
							if (message.error) {
								reject(new Error(message.error));
								return;
							}

							await exchangeCode(message.code);
							resolve();
						} catch (e: any) {
							reject(e);
						}
					});

					dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
						reject(new Error('Connexion annulée'));
					});
				});
			};
			start().catch(reject);
		});
	};

	const initAuth = async (): Promise<void> => {
		if (isDevMode()) {
			const savedKey = localStorage.getItem(DEV_STORAGE_KEYS.apiKey);
			const savedOrg = localStorage.getItem(DEV_STORAGE_KEYS.organizationId);
			if (savedKey && savedOrg && !isAuthenticated.value) {
				await loginWithApiKey(savedKey, savedOrg).catch(() => {});
			}
			return;
		}

		const accessToken = getStoredAccessToken();
		const refreshToken = getStoredRefreshToken();
		if (!accessToken || !refreshToken) {
			return;
		}

		try {
			if (isTokenExpired()) {
				await refreshAccessToken();
			}
			const orgId = localStorage.getItem(STORAGE_KEYS.organizationId);
			if (orgId) {
				organizationId.value = orgId;
			} else {
				const token = getStoredAccessToken()!;
				const fetchedOrgId = await fetchUserInfo(token);
				organizationId.value = fetchedOrgId;
				localStorage.setItem(STORAGE_KEYS.organizationId, fetchedOrgId);
			}
			isAuthenticated.value = true;
		} catch {
			clearStorage();
		}
	};

	const logout = () => {
		clearStorage();
		clearDevStorage();
		devToken.value = null;
		isAuthenticated.value = false;
		organizationId.value = null;
	};

	const loginWithApiKey = async (apiKey: string, orgId?: string): Promise<void> => {
		if (!isDevMode()) {
			throw new Error('La connexion par clé API est uniquement disponible en mode dev');
		}

		const trimmedApiKey = apiKey.trim();
		const trimmedOrgId = orgId?.trim();
		if (!trimmedApiKey) {
			throw new Error('Clé API manquante');
		}

		devToken.value = trimmedApiKey;
		try {
			organizationId.value = trimmedOrgId || (await fetchDevUserInfo(trimmedApiKey));

			const { useWorkspaceApi } = await import('./useWorkspaceApi');
			await useWorkspaceApi().fetchFolders(organizationId.value);

			localStorage.setItem(DEV_STORAGE_KEYS.apiKey, trimmedApiKey);
			localStorage.setItem(DEV_STORAGE_KEYS.organizationId, organizationId.value);
			isAuthenticated.value = true;
		} catch (e) {
			devToken.value = null;
			organizationId.value = null;
			isAuthenticated.value = false;
			throw e;
		}
	};

	const getToken = async (): Promise<string | null> => {
		if (isDevMode()) {
			return devToken.value;
		}
		if (!getStoredAccessToken()) {
			return null;
		}
		if (isTokenExpired()) {
			try {
				await refreshAccessToken();
			} catch {
				logout();
				return null;
			}
		}
		return getStoredAccessToken();
	};

	return {
		isAuthenticated,
		organizationId,
		login,
		loginWithApiKey,
		logout,
		getToken,
		initAuth
	};
};
